import { useContext, useEffect } from 'react';
import { useForm, useFormState } from 'react-hook-form';

import { Message } from '@components';
import { GlobalContext } from '@contexts';
import {
  EmailField,
  FormFieldset,
  SelectField,
  TextField,
} from '@Forms/fields.jsx';
import { errorsFromMutate } from '@Forms/utils.js';
import { ButtonSpinnerIcon } from '@svgs';

export const UserForm = ({ mutation, groupsData, userData }) => {
  const globals = useContext(GlobalContext);
  const {
    register,
    handleSubmit,
    formState: { errors },
    getFieldState,
    setError,
    setFocus,
    control,
    reset,
    resetField,
    trigger,
    watch,
  } = useForm({
    defaultValues: userData,
    mode: 'onBlur',
  });
  const { isValid, isDirty } = useFormState({ control });

  useEffect(() => {
    // set focus to the name field if this is a new datamodel
    if (!userData) {
      setFocus('first_name');
    }
  }, [setFocus]);

  // list of access group IDs, these are not shown in the UI
  const accessGroupIds = globals.whitelabel.oneoff_access_group_ids
    ? Object.values(globals.whitelabel.oneoff_access_group_ids)
    : [];

  const onSubmit = (data) => {
    if (!data.new_password) {
      // remove password from bundle unless it is changing
      delete data.new_password;
      delete data.confirm_password;
    }

    // set groups data using group_id
    if (data.groups) {
      // has groups data already, existing user
      //
      // keep all the permission groups (accessGroupIds) and
      // then add the normal group
      const newGroups = data.groups.filter((g) =>
        accessGroupIds.includes(g.group.id),
      );
      newGroups.push({
        group: {
          id: data.group_id,
        },
      });
      // overwrite groups
      data.groups = newGroups;
    } else {
      // no groups, set initial on new user
      data.groups = [{ group: { id: data.group_id } }];
    }

    mutation.mutate(data, {
      onError: (error) => {
        // handle server-side validations and errors
        const errorsList = errorsFromMutate(error);
        errorsList.forEach(({ field, error }) => setError(field, error));
      },
      onSuccess: () => {
        if (data.new_password) {
          // reset the password fields
          resetField('new_password');
          resetField('confirm_password');
        }
      },
    });
  };

  // group choices for dropdown
  //
  // only lists non-permission groups (accessGroupIds)
  const groupChoices = groupsData.objects
    .filter((group) => !accessGroupIds.includes(group.id))
    .map((d) => {
      return (
        <option key={d.id} value={d.id}>
          {d.name}
        </option>
      );
    });

  const passwordsMatch = (value, fields) => {
    if (
      watch('new_password') &&
      watch('new_password').length > 7 &&
      value != watch('new_password')
    ) {
      return 'Passwords do not match';
    }
    return true;
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={
        `sb-manage-content-form ` +
        (mutation.isPending ? 'sb-form-disabled' : '')
      }
    >
      <FormFieldset>
        <TextField
          register={register}
          options={{ required: 'This field is required' }}
          error={errors.first_name}
          name="first_name"
          label="First name"
        />
        <TextField
          register={register}
          options={{ required: 'This field is required' }}
          error={errors.last_name}
          name="last_name"
          label="Last name"
        />
        <EmailField
          register={register}
          options={{ required: 'This field is required' }}
          error={errors.email}
          name="email"
          label="Email address"
          autoComplete="off"
          required
        />
      </FormFieldset>
      <FormFieldset legend="Permissions">
        <SelectField
          register={register}
          error={errors.group_id}
          name={`group_id`}
          options={{ required: 'This field is required' }}
          label="Group membership"
          helpText="Set of permissions and access level that is assigned to the User."
        >
          {!userData && <option value="">Select...</option>}
          {groupChoices}
        </SelectField>
      </FormFieldset>
      <FormFieldset>
        <legend>{userData ? 'Change password' : 'Set password'}</legend>
        <TextField
          register={register}
          type="password"
          options={{
            minLength: {
              value: 8,
              message: 'Password must have at least 8 characters',
            },
          }}
          error={errors.new_password}
          name="new_password"
          label={userData ? 'New password' : 'Password'}
          autoComplete="off"
        />
        <TextField
          register={register}
          type="password"
          error={errors.confirm_password}
          options={{ validate: passwordsMatch }}
          name="confirm_password"
          label={userData ? 'Confirm new password' : 'Confim password'}
          autoComplete="off"
        />
      </FormFieldset>
      <div className="sb-manage-content-button-bar">
        <div className="sb-flex">
          {/*<button className="sb-btn-cancel sb-btn" onClick={resetForm}>Cancel</button>*/}
          <button
            type="submit"
            className="sb-btn sb-btn-save"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Saving...' : 'Save changes'}
            {mutation.isPending && <ButtonSpinnerIcon />}
          </button>
        </div>
        {!isValid && isDirty && (
          <Message messageStyle="error" title="Please review your form" />
        )}
        {mutation.isError && (
          <Message
            messageStyle="error"
            title="Error saving changes"
            message="Please review your form"
          />
        )}
        {mutation.isSuccess && (
          <Message messageStyle="success" title="Successfully saved" />
        )}
      </div>
    </form>
  );
};

export default UserForm;
